import axiosInstance from './axiosConfig';

/**
 * AI動画「かんたん作成」API クライアント。
 *
 * 契約は _docs/features/2026-09-28_easy-video-mode.md §4（Project JSON / options）。
 * 生成の進行はサーバー側の自動進行（EasyVideoAutoPilotJob）が担い、画面は
 * GET /easy-videos/{id} のポーリングだけで状態を追う。
 */

export type EasyVideoStatus = 'draft' | 'running' | 'completed' | 'failed';
export type EasyVideoStage = 'script' | 'bgm' | 'storyboards' | 'clips' | 'compose';
export type EasyVideoAssetStatus = 'pending' | 'running' | 'done' | 'failed';
export type EasyVideoAspectRatio = '9:16' | '16:9';
export type EasyVideoRetakeKind = 'storyboard' | 'clip';

export interface EasyVideoProductRef {
  id: number;
  name: string;
  image_url: string | null;
}

export interface EasyVideoAsset {
  status: EasyVideoAssetStatus;
  url: string | null;
  started_at: string | null;
}

export interface EasyVideoScene {
  /** UUID */
  id: string;
  order: number;
  summary: string;
  duration_sec: number;
  storyboard: EasyVideoAsset;
  clip: EasyVideoAsset;
  retakes_left: Record<EasyVideoRetakeKind, number>;
  error_message: string | null;
}

export interface EasyVideoCredits {
  estimated: number;
  cap: number;
  consumed: number;
  balance: number;
  retake_unit: Record<EasyVideoRetakeKind, number>;
}

export interface EasyVideoUserRef {
  id: number;
  name: string;
}

export interface EasyVideoProject {
  /** UUID */
  id: string;
  status: EasyVideoStatus;
  stage: EasyVideoStage | null;
  product: EasyVideoProductRef | null;
  aspect_ratio: EasyVideoAspectRatio;
  target_seconds: number;
  mood: string | null;
  appeal: { label: string } | null;
  structure: { title: string } | null;
  cta_text: string | null;
  credits: EasyVideoCredits;
  bgm: { status: string | null; url: string | null } | null;
  scenes: EasyVideoScene[];
  final_video: {
    status: EasyVideoAssetStatus;
    url: string | null;
    download_url: string | null;
    /** 仕上げ（合成）だけをやり直せるか。true のときだけ recompose を受け付ける */
    recomposable?: boolean;
  } | null;
  error_message: string | null;
  eta_seconds: number | null;
  created_by: EasyVideoUserRef | null;
  created_at: string;
  updated_at: string;
}

/** 一覧用（Project のサブセット。サーバーが省く項目は optional） */
export type EasyVideoProjectSummary = Pick<
  EasyVideoProject,
  | 'id'
  | 'status'
  | 'stage'
  | 'product'
  | 'aspect_ratio'
  | 'target_seconds'
  | 'appeal'
  | 'final_video'
  | 'created_by'
  | 'created_at'
  | 'updated_at'
>;

export interface EasyVideoStructureOption {
  id: string;
  title: string;
  scenes: { summary: string; duration_sec: number }[];
  recommended: boolean;
}

export interface EasyVideoOptions {
  appeals: { id: string; label: string; direction: string | null; recommended: boolean }[];
  moods: { id: string; label: string; recommended: boolean }[];
  durations: number[];
  structures: EasyVideoStructureOption[];
  estimate: { credits_max: number; credits_base: number; balance: number };
}

export interface EasyVideoAnswers {
  aspect_ratio?: EasyVideoAspectRatio;
  target_seconds?: number;
  mood?: string;
  appeal_id?: string | null;
  appeal_free_text?: string | null;
  structure_id?: string | null;
  structure_free_text?: string | null;
  cta_text?: string | null;
}

export type EasyVideoConflict = 'retake_limit' | 'budget_exceeded';

/** 422: 選んだ候補の ID がサーバー側で見つからない（戦略マップの編集など） */
export type EasyVideoStaleChoice = 'appeal_id' | 'structure_id';

export interface EasyVideoCreditShortfall {
  requiredCredits: number | null;
  availableCredits: number | null;
}

const errorCode = (error: any): string | undefined => error?.response?.data?.error;

/** 402 credit_insufficient（axiosConfig が CustomEvent を投げ、リーダーには購入モーダルが開く） */
const extractCreditShortfall = (error: any): EasyVideoCreditShortfall | null => {
  if (error?.response?.status !== 402 && errorCode(error) !== 'credit_insufficient') return null;
  const details = error?.response?.data?.details ?? {};
  return {
    requiredCredits: typeof details.required_credits === 'number' ? details.required_credits : null,
    availableCredits: typeof details.available_credits === 'number' ? details.available_credits : null,
  };
};

/** 409 retake_limit / budget_exceeded を判定する（それ以外は null） */
const extractConflict = (error: any): EasyVideoConflict | null => {
  if (error?.response?.status !== 409) return null;
  const code = errorCode(error);
  return code === 'retake_limit' || code === 'budget_exceeded' ? code : null;
};

/** 422 で appeal_id / structure_id が不一致になった項目を返す（選択肢を取り直して選び直す） */
const extractStaleChoices = (error: any): EasyVideoStaleChoice[] => {
  if (error?.response?.status !== 422) return [];
  const errors = error?.response?.data?.errors ?? {};
  return (['appeal_id', 'structure_id'] as const).filter((key) => key in errors);
};

/**
 * ポーリングを止めるべき失敗（4xx: 403 フラグOFF・404 削除など）なら利用者向けの文言を返す。
 * 5xx・ネットワーク失敗は一時的とみなして null（呼び出し側で再試行する）。
 */
const extractPollingStopMessage = (error: any): string | null => {
  const status = error?.response?.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) return null;
  if (status === 404) return 'この動画は見つかりませんでした（削除された可能性があります）。';
  if (status === 403 && errorCode(error) === 'easy_video_disabled') return '「かんたん作成」は現在ご利用いただけません。';
  return error?.response?.data?.message || 'この動画を表示できませんでした。';
};

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || 'エラーが発生しました';

const easyVideoService = {
  async list(): Promise<EasyVideoProjectSummary[]> {
    const response = await axiosInstance.get('/easy-videos');
    return response.data.data ?? [];
  },

  async create(productId: number): Promise<EasyVideoProject> {
    const response = await axiosInstance.post('/easy-videos', { product_id: productId });
    return response.data.data;
  },

  async get(id: string): Promise<EasyVideoProject> {
    const response = await axiosInstance.get(`/easy-videos/${id}`);
    return response.data.data;
  },

  async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/easy-videos/${id}`);
  },

  async options(id: string): Promise<EasyVideoOptions> {
    const response = await axiosInstance.get(`/easy-videos/${id}/options`);
    return response.data.data;
  },

  async update(id: string, answers: EasyVideoAnswers): Promise<EasyVideoProject> {
    const response = await axiosInstance.patch(`/easy-videos/${id}`, answers);
    return response.data.data;
  },

  async start(id: string): Promise<EasyVideoProject> {
    const response = await axiosInstance.post(`/easy-videos/${id}/start`);
    return response.data.data;
  },

  async retake(id: string, sceneId: string, kind: EasyVideoRetakeKind, hint?: string): Promise<EasyVideoProject> {
    const response = await axiosInstance.post(`/easy-videos/${id}/scenes/${sceneId}/retake`, {
      kind,
      ...(hint ? { hint } : {}),
    });
    return response.data.data;
  },

  /** 仕上げ（合成）だけをやり直す。final_video.status === 'failed' のときに使う（無料） */
  async recompose(id: string): Promise<EasyVideoProject> {
    const response = await axiosInstance.post(`/easy-videos/${id}/recompose`);
    return response.data.data;
  },

  extractCreditShortfall,
  extractConflict,
  extractStaleChoices,
  extractPollingStopMessage,
  extractErrorMessage,
};

export default easyVideoService;
